import { HISTORICAL_METHODS } from '../../consumer/historical/contract';
import type {
  ExternalDay,
  ExternalGroup,
  ExternalPageResult,
  ExternalRow,
  ExternalRun,
} from '../../consumer/historical/external-contract';
import {
  EXTERNAL_TRACKS,
  validExternalGroupId,
  validExternalRunId,
} from '../../consumer/historical/external-contract';
type Validate = (v: unknown) => void;
const invalid = (): never => {
  throw new Error('Invalid ExternalValidationV1 response');
};
const text: Validate = (v) => {
  if (typeof v !== 'string' || v.length > 131072) invalid();
};
const finite: Validate = (v) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) invalid();
};
const count: Validate = (v) => {
  finite(v);
  if (!Number.isSafeInteger(v) || (v as number) < 0) invalid();
};
const bool: Validate = (v) => {
  if (typeof v !== 'boolean') invalid();
};
const time: Validate = (v) => {
  if (
    typeof v !== 'string' ||
    !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z$/.test(v) ||
    !Number.isFinite(Date.parse(v)) ||
    new Date(v).toISOString().slice(0, 10) !== v.slice(0, 10)
  )
    invalid();
};
const hash: Validate = (v) => {
  if (typeof v !== 'string' || !/^([a-f0-9]{40}|[a-f0-9]{64})$/.test(v)) invalid();
};
const nullable =
  (f: Validate): Validate =>
  (v) => {
    if (v !== null) f(v);
  };
const one =
  (...values: unknown[]): Validate =>
  (v) => {
    if (!values.includes(v)) invalid();
  };
const array =
  (f: Validate, max = 1000): Validate =>
  (v) => {
    if (!Array.isArray(v) || v.length > max) invalid();
    (v as unknown[]).forEach(f);
  };
const record =
  (shape: Record<string, Validate>): Validate =>
  (v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) invalid();
    const o = v as Record<string, unknown>;
    if (
      Object.keys(o).length !== Object.keys(shape).length ||
      Object.keys(o).some((k) => !Object.hasOwn(shape, k))
    )
      invalid();
    for (const [k, f] of Object.entries(shape)) f(o[k]);
  };
const cohort = record({
  stationId: text,
  name: text,
  country: text,
  latitude: finite,
  longitude: finite,
  climateContext: text,
  climateEvidence: text,
});
const source = record({
  stationId: text,
  year: count,
  url: text,
  originalHash: hash,
  bytes: count,
  rawRows: count,
  temperatureHours: count,
  windHours: count,
  temperatureCoverage: finite,
  windCoverage: finite,
  snapshotHash: hash,
  qualityJson: text,
  accessedAt: time,
});
const bounds = record({
  trainStart: time,
  trainEnd: time,
  validationStart: time,
  validationEnd: time,
  heldout2024Start: time,
  heldout2024End: time,
  heldout2025Start: time,
  heldout2025End: time,
  embargoHours: count,
});
const summary = record({
  track: one(...EXTERNAL_TRACKS),
  donorStationId: nullable(text),
  climateContext: nullable(text),
  testYear: one(2024, 2025),
  metric: one('TEMPERATURE', 'WIND_SPEED'),
  method: one(...HISTORICAL_METHODS),
  macroGroups: count,
  pairedCount: count,
  microMAE: nullable(finite),
  microRMSE: nullable(finite),
  microBias: nullable(finite),
  microBaselineMAE: nullable(finite),
  microBaselineRMSE: nullable(finite),
  macroMAE: nullable(finite),
  macroRMSE: nullable(finite),
  macroBaselineMAE: nullable(finite),
  macroBaselineRMSE: nullable(finite),
});
const reasoncount = record({
  reason: nullable(text),
  count: count,
});
const month = record({
  month: text,
  n: count,
  mae: nullable(finite),
  rmse: nullable(finite),
  bias: nullable(finite),
  baselineMAE: nullable(finite),
  baselineRMSE: nullable(finite),
});
const interval = record({
  status: text,
  blockDays: count,
  replicates: count,
  seed: count,
  effectiveDays: count,
  possibleBlocks: count,
  maeLow: nullable(finite),
  maeHigh: nullable(finite),
  rmseLow: nullable(finite),
  rmseHigh: nullable(finite),
});
const group = record({
  groupId: (v) => {
    if (typeof v !== 'string' || !validExternalGroupId(v)) invalid();
  },
  stationId: text,
  country: text,
  climateContext: text,
  testYear: one(2024, 2025),
  track: one(...EXTERNAL_TRACKS),
  donorStationId: nullable(text),
  metric: one('TEMPERATURE', 'WIND_SPEED'),
  unit: one('degC', 'm/s'),
  horizonHours: one(1, 3, 6),
  method: one(...HISTORICAL_METHODS),
  modelFreezeSHA: hash,
  totalOrigins: count,
  generated: count,
  evaluable: count,
  pairedCount: count,
  coverage: nullable(finite),
  mae: nullable(finite),
  rmse: nullable(finite),
  bias: nullable(finite),
  pairedMAE: nullable(finite),
  pairedRMSE: nullable(finite),
  pairedBias: nullable(finite),
  baselineMAE: nullable(finite),
  baselineRMSE: nullable(finite),
  baselineBias: nullable(finite),
  baselineMAEDelta: nullable(finite),
  baselineRMSEDelta: nullable(finite),
  relativeMAEDelta: nullable(finite),
  relativeRMSEDelta: nullable(finite),
  eligibility: text,
  utility: text,
  interval: interval,
  unavailableReasons: array(reasoncount),
  months: array(month),
});
const row = record({
  groupId: (v) => {
    if (typeof v !== 'string' || !validExternalGroupId(v)) invalid();
  },
  stationId: text,
  climateContext: text,
  testYear: one(2024, 2025),
  metric: one('TEMPERATURE', 'WIND_SPEED'),
  unit: one('degC', 'm/s'),
  horizonHours: one(1, 3, 6),
  method: one(...HISTORICAL_METHODS),
  track: one(...EXTERNAL_TRACKS),
  donorStationId: nullable(text),
  modelFreezeSHA: hash,
  sourceDatasetRevision: text,
  origin: time,
  targetAt: time,
  inputStart: nullable(time),
  inputEnd: nullable(time),
  inputRefs: array(text),
  targetRefs: array(text),
  predicted: nullable(finite),
  actual: nullable(finite),
  baselinePredicted: nullable(finite),
  status: text,
  reason: nullable(text),
  paired: bool,
});
const day = record({
  groupId: (v) => {
    if (typeof v !== 'string' || !validExternalGroupId(v)) invalid();
  },
  day: (v) => {
    if (
      typeof v !== 'string' ||
      !/^\d{4}-\d{2}-\d{2}$/.test(v) ||
      !Number.isFinite(Date.parse(v)) ||
      new Date(v).toISOString().slice(0, 10) !== v
    )
      invalid();
  },
  n: count,
  absoluteErrorSum: finite,
  squaredErrorSum: finite,
  signedErrorSum: finite,
  baselineAbsoluteErrorSum: finite,
  baselineSquaredErrorSum: finite,
  baselineSignedErrorSum: finite,
});
const run = record({
  schemaVersion: text,
  runId: (v) => {
    if (typeof v !== 'string' || !validExternalRunId(v)) invalid();
  },
  status: text,
  mode: text,
  createdAt: time,
  sourceType: text,
  license: text,
  doi: text,
  frozenInputSHA: hash,
  preregistrationSHA: hash,
  preregistrationHash: hash,
  algorithmSHA: hash,
  localFreezeSHA: hash,
  donorFreezeSHA: hash,
  sourceDatasetRevision: text,
  snapshotHash: hash,
  rowsHash: hash,
  groupsHash: hash,
  daysHash: hash,
  rowCount: count,
  groupCount: count,
  externalValidationUtility: text,
  transferStability: text,
  cohort: array(cohort),
  sources: array(source),
  bounds: bounds,
  parametersJson: text,
  summaries: array(summary),
  limitations: array(text),
});
function validateIdentity(r: {
  metric: string;
  unit: string;
  track: string;
  testYear: number;
  donorStationId: string | null;
}) {
  if (
    r.unit !== (r.metric === 'TEMPERATURE' ? 'degC' : 'm/s') ||
    (r.track === EXTERNAL_TRACKS[1] &&
      (r.testYear !== 2025 || !['AEI0000OMAA', 'AEI0000OMDB'].includes(r.donorStationId ?? ''))) ||
    (r.track === EXTERNAL_TRACKS[0] && r.donorStationId !== null)
  )
    invalid();
}
export function parseExternalGroup(v: unknown): ExternalGroup {
  group(v);
  const g = v as ExternalGroup;
  validateIdentity(g);
  if (
    g.evaluable > g.generated ||
    g.generated > g.totalOrigins ||
    g.pairedCount > g.evaluable ||
    (g.coverage !== null && (g.coverage < 0 || g.coverage > 1)) ||
    [g.mae, g.rmse, g.pairedMAE, g.pairedRMSE, g.baselineMAE, g.baselineRMSE].some(
      (x) => x !== null && x < 0,
    )
  )
    invalid();
  return g;
}
export function parseExternalRow(v: unknown): ExternalRow {
  row(v);
  const r = v as ExternalRow;
  validateIdentity(r);
  if (
    Date.parse(r.targetAt) - Date.parse(r.origin) !== r.horizonHours * 3600000 ||
    (r.inputEnd && Date.parse(r.inputEnd) > Date.parse(r.origin)) ||
    (r.inputStart && r.inputEnd && Date.parse(r.inputStart) > Date.parse(r.inputEnd)) ||
    (r.status === 'EVALUATED' && (r.predicted === null || r.actual === null)) ||
    (r.paired && (r.status !== 'EVALUATED' || r.baselinePredicted === null))
  )
    invalid();
  return r;
}
export function parseExternalRun(v: unknown): ExternalRun {
  run(v);
  const r = v as ExternalRun;
  if (
    r.schemaVersion !== 'GHCNH_EXTERNAL_VALIDATION_V1' ||
    r.sourceType !== 'NOAA_GHCNH_STATION_OBSERVATION' ||
    r.mode !== 'RETROSPECTIVE_HINDCAST' ||
    r.bounds.embargoHours !== 24 ||
    Date.parse(r.bounds.trainEnd) > Date.parse(r.bounds.validationStart) ||
    Date.parse(r.bounds.validationEnd) > Date.parse(r.bounds.heldout2024Start)
  )
    invalid();
  return r;
}
function page<T>(v: unknown, f: Validate, parse: (v: unknown) => T): ExternalPageResult<T> {
  record({ items: array(f, 100), total: count, offset: count, limit: count })(v);
  const p = v as ExternalPageResult<T>;
  if (
    p.limit < 1 ||
    p.limit > 100 ||
    p.offset > 2000000 ||
    p.items.length > p.limit ||
    p.items.length > Math.max(0, p.total - p.offset)
  )
    invalid();
  p.items.forEach(parse);
  return p;
}
export const parseExternalRuns = (v: unknown) => page(v, run, parseExternalRun);
export const parseExternalGroups = (v: unknown) => page(v, group, parseExternalGroup);
export const parseExternalRows = (v: unknown) => page(v, row, parseExternalRow);
export const parseExternalDays = (v: unknown) =>
  page(v, day, (v) => {
    day(v);
    return v as ExternalDay;
  });
